import React from 'react';
import { ArrowLeft, FileText, ShoppingCart, Users, AlertTriangle, Scale, Globe } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';

const TermsOfService = () => {
  const navigate = useNavigate();
  const lastUpdated = 'December 15, 2025';

  return (
    <div className="min-h-screen bg-[#0a0b0c]">
      {/* Header */}
      <div className="bg-[#131416] border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="text-white/70 hover:text-white hover:bg-white/5"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Button>
          <img 
            src="https://customer-assets.emergentagent.com/job_tactical-sports/artifacts/00wc7m32_GWINS%20Gear%20Logo.jpg" 
            alt="GWINS" 
            className="h-8 w-auto"
          />
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <FileText className="h-16 w-16 text-[#00d4aa] mx-auto mb-4" />
          <h1 className="text-4xl font-black text-white uppercase mb-2">Terms of Service</h1>
          <p className="text-white/60">Last updated: {lastUpdated}</p>
        </div>

        <div className="space-y-8 text-white/80">
          {/* Agreement */}
          <section className="bg-[#131416] rounded-xl p-6 border border-white/10">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Scale className="h-5 w-5 text-[#00d4aa]" />
              Agreement to Terms
            </h2>
            <p className="leading-relaxed">
              By accessing or using the GWINS Gear website, community platform, and services, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services. These terms apply to all visitors, users, and others who access or use our services.
            </p>
          </section>

          {/* Use of Services */}
          <section className="bg-[#131416] rounded-xl p-6 border border-white/10">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Globe className="h-5 w-5 text-[#00d4aa]" />
              Use of Our Services
            </h2>
            <div className="space-y-4">
              <p>By using our services, you agree to:</p>
              <ul className="list-disc list-inside space-y-2 text-white/70">
                <li>Be at least 13 years of age (or the minimum age in your jurisdiction)</li>
                <li>Provide accurate and complete information when creating an account</li>
                <li>Maintain the security of your account credentials</li>
                <li>Notify us immediately of any unauthorized use of your account</li>
                <li>Use our services only for lawful purposes</li>
                <li>Not violate any applicable laws or regulations</li>
              </ul>
            </div>
          </section>

          {/* Community Guidelines */}
          <section className="bg-[#131416] rounded-xl p-6 border border-white/10">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Users className="h-5 w-5 text-[#00d4aa]" />
              Community Guidelines
            </h2>
            <p className="mb-4">When using our community features (player profiles, team profiles, messaging, social feed), you agree NOT to:</p>
            <ul className="list-disc list-inside space-y-2 text-white/70">
              <li>Post content that is offensive, harassing, or discriminatory</li>
              <li>Share false or misleading information</li>
              <li>Impersonate others or create fake profiles</li>
              <li>Spam other users with unsolicited messages</li>
              <li>Upload content that infringes on intellectual property rights</li>
              <li>Attempt to hack, exploit, or disrupt our services</li>
              <li>Use the platform for commercial solicitation without permission</li>
            </ul>
            <p className="mt-4 text-white/60 text-sm">
              We reserve the right to remove content and suspend accounts that violate these guidelines.
            </p>
          </section>

          {/* E-Commerce */}
          <section className="bg-[#131416] rounded-xl p-6 border border-white/10">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <ShoppingCart className="h-5 w-5 text-[#00d4aa]" />
              E-Commerce & Purchases
            </h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-white font-semibold mb-2">Orders & Payments</h3>
                <ul className="list-disc list-inside space-y-1 text-white/70">
                  <li>All prices are displayed in USD unless otherwise specified</li>
                  <li>Payments are processed securely through Shopify</li>
                  <li>You agree to pay all charges incurred by you or on your behalf</li>
                  <li>We reserve the right to refuse or cancel orders at our discretion</li>
                </ul>
              </div>
              <div>
                <h3 className="text-white font-semibold mb-2">Shipping & Returns</h3>
                <ul className="list-disc list-inside space-y-1 text-white/70">
                  <li>Shipping times and costs vary by location</li>
                  <li>Custom gear orders may have different return policies</li>
                  <li>Please refer to our Shipping Info and Returns pages for detailed policies</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Intellectual Property */}
          <section className="bg-[#131416] rounded-xl p-6 border border-white/10">
            <h2 className="text-xl font-bold text-white mb-4">Intellectual Property</h2>
            <p className="mb-4">
              All content on this website, including but not limited to logos, designs, text, graphics, images, and software, is the property of GWINS Gear or its licensors and is protected by copyright, trademark, and other intellectual property laws.
            </p>
            <p>
              You may not reproduce, distribute, modify, or create derivative works from any content without our express written permission.
            </p>
          </section>

          {/* User Content */}
          <section className="bg-[#131416] rounded-xl p-6 border border-white/10">
            <h2 className="text-xl font-bold text-white mb-4">User-Generated Content</h2>
            <p className="mb-4">
              By posting content on our platform (including profile information, posts, images, and messages), you:
            </p>
            <ul className="list-disc list-inside space-y-2 text-white/70">
              <li>Retain ownership of your original content</li>
              <li>Grant us a non-exclusive, royalty-free license to use, display, and distribute your content on our platform</li>
              <li>Represent that you have the right to share such content</li>
              <li>Understand that content may be visible to other users based on your privacy settings</li>
            </ul>
          </section>

          {/* Disclaimers */}
          <section className="bg-[#131416] rounded-xl p-6 border border-white/10">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-[#00d4aa]" />
              Disclaimers & Limitation of Liability
            </h2>
            <div className="space-y-4 text-white/70">
              <p>
                <strong className="text-white">AS IS:</strong> Our services are provided "as is" without warranties of any kind, either express or implied.
              </p>
              <p>
                <strong className="text-white">LIMITATION:</strong> To the maximum extent permitted by law, GWINS Gear shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of our services.
              </p>
              <p>
                <strong className="text-white">THIRD PARTIES:</strong> We are not responsible for the actions, content, or services of third parties linked from our website.
              </p>
            </div>
          </section>

          {/* Termination */}
          <section className="bg-[#131416] rounded-xl p-6 border border-white/10">
            <h2 className="text-xl font-bold text-white mb-4">Termination</h2>
            <p>
              We may terminate or suspend your account and access to our services immediately, without prior notice, for any reason, including breach of these Terms. Upon termination, your right to use our services will immediately cease.
            </p>
          </section>

          {/* Governing Law */}
          <section className="bg-[#131416] rounded-xl p-6 border border-white/10">
            <h2 className="text-xl font-bold text-white mb-4">Governing Law</h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of the United States, without regard to conflict of law principles. Any disputes arising from these Terms will be resolved in the courts of the United States.
            </p>
          </section>

          {/* Changes */}
          <section className="bg-[#131416] rounded-xl p-6 border border-white/10">
            <h2 className="text-xl font-bold text-white mb-4">Changes to Terms</h2>
            <p>
              We reserve the right to modify these Terms at any time. We will notify users of significant changes by posting a notice on our website. Your continued use of our services after changes are posted constitutes acceptance of the modified Terms.
            </p>
          </section>

          {/* Contact */}
          <section className="bg-[#131416] rounded-xl p-6 border border-white/10">
            <h2 className="text-xl font-bold text-white mb-4">Contact Us</h2>
            <p>
              If you have any questions about these Terms of Service, please contact us at:
            </p>
            <p className="mt-4 text-[#00d4aa] font-semibold">info@gwinsgear.com</p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
